// PaidMediaPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from './paidxs.png'

const PaidMediaPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const paidMediaStats = [
        { number: '63%', label: 'of customers click on Google Ads when shopping online' },
        { number: '2.8x', label: 'higher conversion rate from targeted paid campaigns' },
        { number: '75%', label: 'increase in brand awareness with consistent paid media' },
        { number: '45%', label: 'lower cost per acquisition with optimized campaigns' }
    ];

    const paidMediaApproaches = [
        {
            icon: 'fas fa-search-dollar',
            title: 'PPC (Pay-Per-Click) Advertising',
            desc: 'Strategic search advertising on Google, Bing, and search platforms with keyword strategy, bid management, shopping campaigns, and performance tracking for maximum ROI.'
        },
        {
            icon: 'fas fa-thumbs-up',
            title: 'Paid Social Media Advertising',
            desc: 'Targeted campaigns across Facebook, Instagram, LinkedIn, Twitter, and Pinterest with precise audience targeting, retargeting strategies, and social engagement optimization.'
        },
        {
            icon: 'fas fa-robot',
            title: 'Programmatic Advertising',
            desc: 'Automated media buying and placement across thousands of websites and apps with real-time bidding, display/video advertising, and cross-channel campaign management.'
        },
        {
            icon: 'fas fa-facebook',
            title: 'Meta Advertising Specialization',
            desc: 'Specialized Facebook and Instagram campaigns leveraging powerful targeting, brand awareness strategies, lead generation, and dynamic product ads for e-commerce.'
        },
        {
            icon: 'fas fa-handshake',
            title: 'PPC Consultancy & Strategic Planning',
            desc: 'Expert campaign audits, strategic planning, team training, performance benchmarking, and KPI development for optimizing existing paid media efforts.'
        },
        {
            icon: 'fas fa-bullseye',
            title: 'Performance Optimization & Analytics',
            desc: 'Continuous campaign monitoring, data-driven optimization, A/B testing, conversion tracking, and transparent performance reporting for measurable growth.'
        }
    ];

    const benefits = [
        ['Immediate Visibility & Reach', 'Get in front of your target audience from day one with controlled budget allocation'],
        ['Precise Targeting Capabilities', 'Reach specific demographics, interests, behaviors, and geographic locations with accuracy'],
        ['Measurable ROI & Performance Tracking', 'Track every advertising dollar to specific results and optimize for maximum return'],
        ['Scalable Growth Strategy', 'Start with conservative testing and expand campaigns based on proven performance data'],
        ['Competitive Market Advantage', 'Outperform competitors with smarter bidding strategies and creative optimization']
    ];

    const targetBusinesses = [
        'E-commerce brands seeking to drive immediate product sales and customer acquisition',
        'B2B companies looking for qualified leads and pipeline development through targeted advertising',
        'Startups launching new products or services needing rapid market visibility and validation',
        'Local businesses targeting specific geographic areas for foot traffic and local engagement',
        'Established brands maintaining market presence and defending against competitive threats',
        'Seasonal businesses with time-sensitive promotions requiring precise timing and targeting'
    ];

    const paidMediaQuestions = [
        'How can you reach your ideal customers at the exact moment they\'re ready to buy?',
        'What advertising channels deliver the best ROI for your specific business objectives?',
        'How do you optimize ad spend across multiple platforms for maximum efficiency?',
        'What targeting strategies ensure your ads reach the most relevant audiences?',
        'How can you scale successful campaigns while maintaining profitability?'
    ];

    return (
        <div className="paidmedia sv-page">
            <div className="sv-decorative-image-container">

            </div>

            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Paid Media</span> Services
                    </h1>
                    <p>Strategic advertising solutions that deliver immediate visibility, targeted reach, and measurable ROI</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In today's competitive digital landscape, paid media offers the precision, speed, and scalability to reach your target audience exactly when they're ready to engage.
                                </p>
                                <p className="sv-description">
                                    While organic strategies build long-term authority, <strong>paid media</strong> delivers immediate visibility and measurable conversions. At Reachtix, we don't just run ads — we build strategic paid media campaigns that align with your business objectives, whether you're launching a new product, driving sales, or building brand awareness.
                                </p>
                                <p className="sv-description">
                                    With organic reach declining and competition intensifying, paid media has become essential for businesses that want to control their growth trajectory. Our approach combines data-driven strategy with creative excellence to ensure every advertising dollar works harder for your business.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Paid Media Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-chart-line mr-2"></i>
                                        Get Media Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Paid Media Advantage</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {paidMediaStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Paid Media Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Paid Media?</h2>
                                <p className="sv-section-description">
                                    Paid media encompasses all forms of digital advertising where businesses pay to display promotional content across search engines, social platforms, websites, and mobile apps. It provides immediate access to target audiences with precise targeting, budget control, and measurable performance tracking.
                                </p>
                                <p className="sv-section-description">
                                    Unlike organic marketing, paid media offers predictable, scalable results that can be optimized in real-time based on performance data, making it essential for businesses seeking accelerated growth and competitive advantage in crowded markets.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Paid Media Questions We Address:</h4>
                                    {paidMediaQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Paid Media Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Paid Media Drives Business Growth</h2>
                                <p className="sv-section-description">
                                    Paid media transforms advertising spend into measurable business outcomes, enabling organizations to accelerate growth, validate markets, and optimize customer acquisition strategies with precision and control.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bolt"></i>
                                        <h5>Immediate Results</h5>
                                        <p>Launch campaigns that deliver visibility and conversions from day one</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Precision Targeting</h5>
                                        <p>Reach specific audiences based on demographics, interests, and behavior</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Measurable ROI</h5>
                                        <p>Track performance with granular data and optimize for maximum return</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-expand"></i>
                                        <h5>Scalable Growth</h5>
                                        <p>Test, validate, and scale successful campaigns based on performance data</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Paid Media Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Paid Media Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer comprehensive paid media solutions across all major advertising platforms, tailored to your specific business goals, target audience, and competitive landscape.
                                </p>

                                <div className="sv-approaches-grid">
                                    {paidMediaApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Paid Media with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Paid Media?</h2>
                                <p className="sv-section-description">
                                    Paid media delivers maximum value for organizations seeking accelerated growth, market validation, and measurable customer acquisition:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're entering new markets, launching innovative products, or defending market share against competitors, strategic paid media provides the visibility and results needed to achieve your business objectives.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Accelerate Your Growth with Paid Media?</h2>
                                <p className="sv-cta-description">
                                    In today's fast-paced digital world, waiting for organic growth means missing opportunities. Paid media puts your brand in front of the right people at the right time — delivering measurable results from day one.
                                </p>
                                <p className="sv-cta-description">
                                    Whether you're looking to drive sales, generate leads, or build brand awareness, our <strong>paid media</strong> experts will create campaigns that deliver real business impact with measurable ROI and scalable growth potential.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-ad mr-2"></i>
                                        Launch Your Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Free Media Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PaidMediaPage;